import { Component, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { WizardSection } from '@app/models/project-setup.models';
import { WizardStore } from '@app/stores/project-setup/wizard.store';
import { GenericStore } from '@app/stores/generic.store';
import { GenericProjectStore } from '@app/stores/projects/generic-project.store';
import { CardComponent } from '@app/ui-components/card/card.component';
import { FormArrayCardComponent } from '@app/ui-components/form-array-card/form-array-card.component';
import { InfoLabelComponent } from '@app/ui-components/info-label/info-label.component';
import { ValidatedDropdownComponent } from '@app/ui-components/validated-dropdown/validated-dropdown.component';
import { ValidatedInputComponent } from '@app/ui-components/validated-input/validated-input.component';
import { ValidatedTextAreaComponent } from '@app/ui-components/validated-textarea/validated-textarea.component';
import { TranslatePipe } from '@ngx-translate/core';
import { isBooleanLikeTrue } from '@app/helpers/form.helper';
import { SectionGFormGroup } from '../+helpers/section-g-form.helper';
import { getApplicableInterestRatesFormGroup } from '../../applicable-interest-rates/applicable-interest-rates-form.helper';
import { ApplicableInterestRatesComponent } from '../../applicable-interest-rates/applicable-interest-rates.component';
import { LegalPersonValidatedFormComponent } from '../../legal-person-validated-form/legal-person-validated-form.component';
import { getLegalEntityWithRoleFormGroup } from '../+helpers/section-g-form.helper';
import { GuarantorFormComponent } from '../../guarantor-form/guarantor-form.component';
import { getGuarantorFormGroup } from '../../guarantor-form/guarantor-form.helper';
import { NotCompletedLoanFormComponent } from '../../not-completed-loan-form/not-completed-loan-form.component';
import { getNotCompletedLoanFormGroup } from '../../not-completed-loan-form/not-completed-loan-form.helper';

/** Entity names for not-completed loan: type "Loan", connection "NotPerformingLoan". */
const NOT_COMPLETED_LOAN_TYPE_NAME = 'Loan';
const NOT_COMPLETED_LOAN_CONNECTION_NAME = 'NotPerformingLoan';

@Component({
  selector: 'app-section-g',
  imports: [
    ReactiveFormsModule,
    CardComponent,
    ValidatedInputComponent,
    ValidatedDropdownComponent,
    ValidatedTextAreaComponent,
    ApplicableInterestRatesComponent,
    InfoLabelComponent,
    TranslatePipe,
    FormArrayCardComponent,
    LegalPersonValidatedFormComponent,
    GuarantorFormComponent,
    NotCompletedLoanFormComponent,
  ],
  templateUrl: './section-g.component.html',
  styleUrl: './section-g.component.scss',
})
export class SectionGComponent implements OnInit {
  genericStore = inject(GenericStore);
  genericProjectStore = inject(GenericProjectStore);
  wizardStore = inject(WizardStore);
  projectId = input.required<string>();
  protected translationKey = 'projectWizard.sectionGForm.';

  protected readonly isYes = isBooleanLikeTrue;

  constructor(private fb: FormBuilder, private destroyRef: DestroyRef) {}

  ngOnInit(): void {
    this.wizardStore
      .getSection(this.projectId(), WizardSection.G)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private get form(): FormGroup<SectionGFormGroup> | null {
    return this.wizardStore.G()?.form ?? null;
  }

  protected removeRow($event: number) {
    this.form?.controls.loanInterests.removeAt($event);
  }

  protected addNewApplicbleInterestRate(): void {
    this.form?.controls.loanInterests.push(
      getApplicableInterestRatesFormGroup(this.fb)
    );
  }

  protected removeCollateralProvider($event: number) {
    this.form?.controls.collateralProviders.removeAt($event);
  }

  protected addCollateralProvider() {
    this.form?.controls.collateralProviders.push(
      getLegalEntityWithRoleFormGroup(this.fb, 'ProjectGuarantor')
    );
  }

  protected removeGuarantor($event: number) {
    this.form?.controls.guarantors.removeAt($event);
  }

  protected addGuarantor() {
    this.form?.controls.guarantors.push(
      getGuarantorFormGroup(this.fb, 'ProjectGuarantor')
    );
  }

  protected addGuaranteeProvider() {
    this.form?.controls.guarateeProviders.push(
      getLegalEntityWithRoleFormGroup(this.fb, 'ProjectWarranty')
    );
  }

  protected removeGuaranteeProvider($event: number) {
    this.form?.controls.guarateeProviders.removeAt($event);
  }

  protected removeNotCompletedLoan($event: number) {
    this.form?.controls.notCompletedLoans.removeAt($event);
  }

  protected addNotCompletedLoan() {
    const loanType = this.genericStore
      .financialAssetTypeEntities()
      .find((e) => e.name === NOT_COMPLETED_LOAN_TYPE_NAME);
    const connectionType = this.genericProjectStore
      .financialAssetConnectionTypeToProjectEntities()
      .find((e) => e.name === NOT_COMPLETED_LOAN_CONNECTION_NAME);
    const typeId = loanType?.id ?? 0;
    const connectionTypeId = connectionType?.id ?? 3;
    this.form?.controls.notCompletedLoans.push(
      getNotCompletedLoanFormGroup(this.fb, typeId, connectionTypeId)
    );
  }
}
